import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { AppearanceChoice } from "./AppearanceChoice";
import { IconAlertCircle, IconAlertTriangle, IconChevronLeft, IconChevronRight, IconDots, IconFileText, IconListTree, IconSparkles, IconX } from "@tabler/icons-react";
import { testIds } from "@/testIds";
import { askContext, blockLocation, blockNoun, nounLabel, citationCard, referenceCard, tapAction, type AskContext, type BibEntry, type ReaderBlock, type RefCard } from "@/lib/mobileReader";
import { ASK_NEW_CHAT_EVENT } from "@/components/editor/AskLiliaPanel";
import { AI_CHANGES_EVENT, blockText, buildChanges, changesHeadline, resolveRefs, type AiChangesDetail, type BlockChange } from "@/lib/mobileChanges";
import { MobileShareSheet, type ShareState } from "./MobileShareSheet";
import type { DocumentCheck } from "./useDocumentCheck";
import { issueSummary } from "@/lib/documentCheck";
import i18n from "i18next";
import { LATEX_TABS, useLatexTab, type LatexTab } from "@/components/editor/preview/LatexTabBar";
import styles from "./MobileReader.module.scss";

const M = testIds.mobile;

export interface MobileReaderProps {
  title: string;
  /** The reader may read but not change the document (Ask Lilia answers only). */
  viewOnly: boolean;
  /** "Owner" or the owner's name, for the view-only line. */
  ownerLabel?: string;
  blocks: readonly ReaderBlock[];
  /** The document, read-only (the editor's own renderer). */
  body: ReactNode;
  /** The outline; `go` scrolls to a block and closes the sheet. */
  renderOutline: (go: (blockId: string) => void) => ReactNode;
  /** The compiled PDF, full-screen. */
  renderPreview: (opts: { seeIssues: () => void }) => ReactNode;
  /** View LaTeX (Olivia, 10 Oct): Preview's second view; a block's lines highlighted. */
  /** The LaTeX view, on the tab picked in the preview's tab row (DESIGN-14). */
  renderLatex?: (opts: { highlightBlockId: string | null; tab: LatexTab; onTab: (tab: LatexTab) => void }) => ReactNode;
  /** Ask Lilia, in its sheet, with the tapped block as context. */
  renderAsk: (opts: { context: AskContext | null; clearContext: () => void; close: () => void; prefill?: string; autoFocus?: boolean }) => ReactNode;
  onBack: () => void;
  onScrollToBlock: (blockId: string) => void;
  /** The document, so Ask Lilia's change events for other documents are ignored. */
  documentId: string;
  /** The document's bibliography, for citation cards. */
  bibliography?: readonly BibEntry[];
  /** ⋯ → Share… (Olivia, 10 Oct). */
  share?: { canManage: boolean; ownerId?: string; ownerName?: string; initial: ShareState };
  /** ⋯ → Check document (Olivia, 10 Oct): runs on open, a red dot on ⋯ for errors. */
  check?: DocumentCheck;
  /** The publisher check for a document with a venue (25 Oct 2026): shown in the check sheet. */
  publisherCheck?: { publisher: string; status: string; findings: { severity: "error" | "warning" | "info"; code: string; message: string }[] } | null;
  /** ⋯ → Look… (Olivia, 10 Oct): theme cards over the live page. `close` after Apply or Cancel. */
  renderLook?: (opts: { close: () => void }) => ReactNode;
  /** Called when the Look sheet is dismissed without Apply or Cancel (Back, tap outside): put the saved look back. */
  onLookDismiss?: () => void;
  /** ⋯ → Page & settings… */
  renderSettings?: () => ReactNode;
  /**
   * Just created: Ask Lilia opens at half height, the document still in view,
   * and the keyboard waits for a tap. No suggestions (the user, 11 Oct: they
   * covered the screen).
   */
  askOnArrival?: boolean;
  /** ⋯ → Document details. */
  details?: { owner?: string; updatedAt?: string; words?: number };
  /** Undo all: back to the version taken before Lilia's edit. */
  onUndoAll: (versionId: string) => Promise<void>;
  /** Undo one change: the block as it was (or gone, if Lilia added it). */
  onUndoChange: (change: BlockChange) => Promise<void>;
}

interface Review {
  detail: AiChangesDetail;
  /** The blocks as they were when Lilia started editing. */
  before: readonly ReaderBlock[];
  /** Changes the reader undid one by one. */
  undone: string[];
}

type Sheet = null | "outline" | "ask" | "more" | "share" | "details" | "check" | "look" | "settings";

/**
 * The mobile reader (Olivia, design-handoffs/2026-10-10-mobile-reader): one
 * screen, no tab bar. The document fills the phone and never takes a caret;
 * Ask Lilia is a sheet over it; Preview opens full-screen from the top bar. The
 * keyboard opens only from Ask Lilia's input.
 */
export function MobileReader(props: MobileReaderProps) {
  const { t } = useTranslation("mobile");
  const { title, viewOnly, blocks } = props;
  const [sheet, setSheet] = useState<Sheet>(props.askOnArrival ? "ask" : null);
  const [preview, setPreview] = useState(false);
  /** Preview shows the PDF or its LaTeX; a block opened from its chip is highlighted. */
  const [previewMode, setPreviewMode] = useState<"pdf" | "latex">("pdf");
  const [latexBlock, setLatexBlock] = useState<string | null>(null);
  const [latexTab, setLatexTab] = useLatexTab();
  const [selected, setSelected] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const tapStart = useRef<{ x: number; y: number; t: number } | null>(null);
  const swipeStart = useRef<{ x: number; y: number; top: number } | null>(null);
  /** A tapped citation, footnote or cross-reference: a card in place of the Ask bar. */
  const [refCard, setRefCard] = useState<RefCard | null>(null);
  /** A tapped figure, table or equation, full-screen: its id and its rendered markup. */
  const [viewer, setViewer] = useState<{ id: string; html: string; type: string } | null>(null);
  const [zoomed, setZoomed] = useState(false);

  // ── Check document (Olivia, 10 Oct) ──────────────────────────────────────────
  // A tap on an issue closes the sheet, scrolls to its block and marks it (red for
  // an error, amber for a warning); the card steps through the issues.
  const issues = useMemo(() => props.check?.issues ?? [], [props.check?.issues]);
  const errorCount = issues.filter((i) => i.severity === "error").length;
  const [issueAt, setIssueAt] = useState<number | null>(null);
  const [askPrefill, setAskPrefill] = useState<string | undefined>(undefined);
  // A new document opens on Ask Lilia at half height, until the sheet closes.
  const [arrivalHalf, setArrivalHalf] = useState(!!props.askOnArrival);
  useEffect(() => {
    if (sheet !== "ask") setArrivalHalf(false);
  }, [sheet]);
  const activeIssue = issueAt !== null ? issues[issueAt] ?? null : null;
  useEffect(() => {
    if (issueAt !== null && issueAt >= issues.length) setIssueAt(issues.length ? issues.length - 1 : null);
  }, [issueAt, issues.length]);
  const goToIssue = (i: number) => {
    const iss = issues[i];
    if (!iss) return;
    setSheet(null);
    setRefCard(null);
    setSelected(null);
    setIssueAt(i);
    if (iss.blockId) props.onScrollToBlock(iss.blockId);
  };
  const askAboutIssue = () => {
    if (!activeIssue) return;
    if (activeIssue.blockId) setSelected(activeIssue.blockId);
    setAskPrefill(
      viewOnly
        ? t("reader.issue.promptWhy", { message: activeIssue.message })
        : t(`reader.issue.promptFix_${activeIssue.severity === "error" ? "error" : "warning"}`, { message: activeIssue.message }),
    );
    setIssueAt(null);
    setSheet("ask");
  };

  // ── Ask Lilia's changes (README 4) ──────────────────────────────────────────
  // The blocks when Lilia starts editing are the "before"; when it reports the
  // change, the sheet drops to the summary card and the changed blocks are marked.
  const blocksRef = useRef(blocks);
  blocksRef.current = blocks;
  const snapshot = useRef<readonly ReaderBlock[] | null>(null);
  const [review, setReview] = useState<Review | null>(null);
  const [step, setStep] = useState(0);
  const [diffFor, setDiffFor] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const onEditing = (e: Event) => {
      const d = (e as CustomEvent<{ documentId?: string; active?: boolean }>).detail;
      if (d?.documentId === props.documentId && d.active) snapshot.current = blocksRef.current;
    };
    const onChanges = (e: Event) => {
      const d = (e as CustomEvent<AiChangesDetail>).detail;
      if (!d || d.documentId !== props.documentId || !d.changedBlockIds.length) return;
      setReview({ detail: d, before: snapshot.current ?? blocksRef.current, undone: [] });
      setSelected(null);
      setStep(0);
      setDiffFor(null);
      setSheet(null);
    };
    window.addEventListener("lilia:ai-editing", onEditing);
    window.addEventListener(AI_CHANGES_EVENT, onChanges);
    return () => {
      window.removeEventListener("lilia:ai-editing", onEditing);
      window.removeEventListener(AI_CHANGES_EVENT, onChanges);
    };
  }, [props.documentId]);
  // Live: the store reloads after the edit, so "after" is the blocks as they are now.
  const changes = useMemo(
    () => (review ? buildChanges(review.before, blocks, review.detail.changedBlockIds).filter((c) => !review.undone.includes(c.blockId)) : []),
    [review, blocks],
  );
  const current = changes[Math.min(step, Math.max(0, changes.length - 1))] ?? null;
  const marked = useMemo(() => changes.filter((c) => c.kind !== "removed").map((c) => c.blockId), [changes]);
  const goToChange = (i: number) => {
    const c = changes[i];
    setStep(i);
    if (c && c.kind !== "removed") props.onScrollToBlock(c.blockId);
    // A removed block has no place in the page: its before/after is where it is shown.
    if (c?.kind === "removed") setDiffFor(c.blockId);
  };
  const keep = () => {
    setReview(null);
    setDiffFor(null);
  };
  const undoAll = async () => {
    if (!review?.detail.undoVersionId) return;
    setBusy(true);
    try {
      await props.onUndoAll(review.detail.undoVersionId);
      keep();
    } finally {
      setBusy(false);
    }
  };
  const undoOne = async (c: BlockChange) => {
    setBusy(true);
    try {
      await props.onUndoChange(c);
      setReview((r) => (r ? { ...r, undone: [...r.undone, c.blockId] } : r));
      setDiffFor(null);
    } finally {
      setBusy(false);
    }
  };
  // The marks carry an attribute too, for tests and assistive tech.
  useEffect(() => {
    const host = bodyRef.current;
    if (!host) return;
    host.querySelectorAll("[data-change-mark]").forEach((el) => {
      el.removeAttribute("data-change-mark");
      el.removeAttribute("aria-current");
    });
    for (const id of marked) {
      const el = host.querySelector(`[data-block-id="${id.replace(/["\\]/g, "\\$&")}"]`);
      if (!el) continue;
      el.setAttribute("data-change-mark", "true");
      if (current?.blockId === id) el.setAttribute("aria-current", "true");
    }
  });
  const diff = diffFor ? changes.find((c) => c.blockId === diffFor) ?? null : null;

  // The system Back closes what is open (the before/after, the viewer, Preview, a
  // sheet, a reference card) before it leaves the document (Olivia, 10 Oct). One
  // history entry stands for "something is open"; Back pops it and closes the top layer.
  const lookDismiss = useRef(props.onLookDismiss);
  lookDismiss.current = props.onLookDismiss;
  const layerOpen = !!(diffFor || viewer || preview || sheet || refCard || issueAt !== null);
  const pushed = useRef(false);
  useEffect(() => {
    if (layerOpen && !pushed.current) {
      window.history.pushState({ ...(window.history.state ?? {}), liliaReaderLayer: true }, "");
      pushed.current = true;
    } else if (!layerOpen && pushed.current) {
      // Closed from the screen: take back the entry we added.
      pushed.current = false;
      if (window.history.state?.liliaReaderLayer) window.history.back();
    }
  }, [layerOpen]);
  useEffect(() => {
    const onPop = () => {
      if (!pushed.current) return;
      pushed.current = false;
      if (diffFor) setDiffFor(null);
      else if (viewer) setViewer(null);
      else if (preview) setPreview(false);
      else if (sheet) {
        if (sheet === "look") lookDismiss.current?.();
        setSheet(null);
      }
      else if (refCard) setRefCard(null);
      else if (issueAt !== null) setIssueAt(null);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [diffFor, viewer, preview, sheet, refCard, issueAt]);
  // References as the page shows them: the numbers come from the rendered references.
  const refLabels = (): Map<string, string> => {
    const map = new Map<string, string>();
    bodyRef.current?.querySelectorAll<HTMLElement>("[data-ref-label]").forEach((el) => {
      const key = el.getAttribute("data-ref-label");
      const text = el.textContent?.trim();
      if (key && text && !map.has(key)) map.set(key, text);
    });
    return map;
  };
  const shown = (text: string) => resolveRefs(text, refLabels());
  /** The block as the page shows it now (references resolved), or its text. */
  const liveText = (id: string, fallback: string) => {
    const el = bodyRef.current?.querySelector<HTMLElement>(`[data-block-id="${id.replace(/["\\]/g, "\\$&")}"]`);
    const t = el?.textContent?.replace(/\s+/g, " ").trim();
    return t || shown(fallback);
  };

  const context = useMemo(() => (selected ? askContext(blocks, selected) : null), [blocks, selected]);
  // A selected block that left the document (Lilia removed it) is no longer selected.
  useEffect(() => {
    if (selected && !blocks.some((b) => b.id === selected)) setSelected(null);
  }, [blocks, selected]);

  /** A tap on the document: select a text block as Ask Lilia's context (README, *Taps*). */
  const onTap = useCallback(
    (e: React.MouseEvent | React.PointerEvent) => {
      const target = e.target as HTMLElement;
      // A citation, a footnote mark or a cross-reference: a card; the first tap never jumps.
      const cite = target.closest<HTMLElement>("[data-cite-key]");
      if (cite) {
        setRefCard(citationCard(props.bibliography ?? [], cite.getAttribute("data-cite-key") ?? ""));
        return;
      }
      const note = target.closest<HTMLElement>("[data-footnote-content]");
      if (note) {
        setRefCard({ kind: "footnote", number: note.getAttribute("data-footnote-number"), text: (note.getAttribute("data-footnote-content") ?? "").replace(/<[^>]+>/g, "").trim() });
        return;
      }
      const ref = target.closest<HTMLElement>("[data-ref-label]");
      if (ref) {
        setRefCard(referenceCard(blocks, ref.getAttribute("data-ref-label") ?? "", ref.textContent?.trim() || "??"));
        return;
      }
      setRefCard(null);
      // Links and controls inside blocks keep their own behaviour.
      if (target.closest("a, button, [role=button], input, textarea, select")) return;
      const el = target.closest<HTMLElement>("[data-block-id]");
      const id = el?.getAttribute("data-block-id");
      if (id && marked.includes(id)) {
        setDiffFor(id);
        setStep(Math.max(0, changes.findIndex((c) => c.blockId === id)));
        return;
      }
      const block = id ? blocks.find((b) => b.id === id) : undefined;
      if (block && el && tapAction(block.type) === "view") {
        // The block as the page draws it, full-screen; editing controls are hidden there too.
        setViewer({ id: block.id, html: el.outerHTML, type: block.type });
        setZoomed(false);
        setSelected(null);
        return;
      }
      if (!block || tapAction(block.type) !== "select") {
        setSelected(null);
        return;
      }
      setSelected((cur) => (cur === block.id ? null : block.id));
    },
    [blocks, marked, changes, props.bibliography],
  );

  const go = useCallback(
    (blockId: string) => {
      setSheet(null);
      props.onScrollToBlock(blockId);
    },
    [props],
  );

  const selectedNoun = context?.noun ?? (selected ? blockNoun(blocks.find((b) => b.id === selected)?.type ?? "") : "paragraph");

  return (
    <div className={styles.reader} data-testid={M.read}>
      {/* The selected block's outline: CSS, so the editor's own DOM is never touched. */}
      {selected && (
        <style>{`[data-testid="${M.readBody}"] [data-block-id="${selected.replace(/["\\]/g, "\\$&")}"] { outline: 1.5px solid var(--lilia-primary, #0a76a4); outline-offset: 4px; border-radius: 4px; background: var(--lilia-primary-subtle, rgba(10, 118, 164, 0.08)); }`}</style>
      )}

      {marked.length > 0 && (
        <style>{marked
          .map((id) => `[data-testid="${M.readBody}"] [data-block-id="${id.replace(/["\\]/g, "\\$&")}"] { box-shadow: inset 3px 0 0 var(--lilia-primary, #0a76a4); background: var(--lilia-primary-subtle, rgba(10, 118, 164, 0.08)); padding-left: 10px; border-radius: 2px; }`)
          .join("\n")}</style>
      )}

      {activeIssue?.blockId && (
        <style>{`[data-testid="${M.readBody}"] [data-block-id="${activeIssue.blockId.replace(/["\\]/g, "\\$&")}"] { outline: 2px solid ${activeIssue.severity === "error" ? "var(--lilia-danger, #c62828)" : "var(--lilia-warning, #b7791f)"}; outline-offset: 4px; border-radius: 4px; background: ${activeIssue.severity === "error" ? "var(--lilia-danger-subtle, rgba(198, 40, 40, 0.06))" : "var(--lilia-warning-subtle, rgba(183, 121, 31, 0.08))"}; }`}</style>
      )}

      <header className={styles.topbar} data-testid={M.readTopbar}>
        <button type="button" className={styles.iconBtn} aria-label={t("reader.top.allDocuments")} title={t("reader.top.allDocuments")} data-testid={M.readBack} onClick={props.onBack}>
          <IconChevronLeft size={22} />
        </button>
        <div className={styles.titleBlock}>
          <span className={styles.title}>{title || t("docs.untitled")}</span>
          {viewOnly && <span className={styles.titleSub}>{t("reader.top.viewOnly")}{props.ownerLabel ? ` · ${props.ownerLabel}` : ""}</span>}
        </div>
        <button type="button" className={styles.iconBtn} aria-label={t("reader.top.outline")} data-testid={M.readOutline} onClick={() => setSheet("outline")}>
          <IconListTree size={21} />
        </button>
        <button
          type="button"
          className={styles.iconBtn}
          aria-label={t("reader.top.previewPdf")}
          data-testid={M.readPreview}
          onClick={() => {
            setPreviewMode("pdf");
            setLatexBlock(null);
            setPreview(true);
          }}
        >
          <IconFileText size={21} />
        </button>
        <button type="button" className={styles.iconBtn} aria-label={errorCount ? t("reader.top.moreErrors", { count: errorCount }) : t("reader.top.more")} aria-haspopup="menu" data-testid={M.readMore} onClick={() => setSheet(sheet === "more" ? null : "more")}>
          <IconDots size={21} />
          {errorCount > 0 && <span className={styles.moreDot} data-testid={M.moreDot} aria-hidden="true" />}
        </button>
      </header>

      <div
        ref={bodyRef}
        className={styles.body}
        data-testid={M.readBody}
        // Read-only: no input mode, so no keyboard even if something takes focus.
        inputMode="none"
        // A tap is read from the pointer, not from click: on a touch screen the
        // editor takes the touch, and no click follows on some blocks (a table).
        onPointerDown={(e) => {
          tapStart.current = { x: e.clientX, y: e.clientY, t: Date.now() };
        }}
        onPointerUp={(e) => {
          const s0 = tapStart.current;
          tapStart.current = null;
          if (!s0 || Math.hypot(e.clientX - s0.x, e.clientY - s0.y) > 10 || Date.now() - s0.t > 600) return;
          onTap(e);
        }}
        // The blocks' own click handlers (a table's "click to edit") are for the editor,
        // not the reader. Links keep theirs.
        onClickCapture={(e) => {
          if (!(e.target as HTMLElement).closest("a")) e.stopPropagation();
        }}
      >
        {props.body}
      </div>

      <div className={styles.footer}>
        {copied && <div className={styles.toast} role="status">{t("reader.linkCopied")}</div>}
        {context && (
          <div className={styles.chipRow}>
            <button type="button" className={styles.chip} data-testid={M.askChip} onClick={() => setSheet("ask")}>
              {t(`reader.askAbout.${selectedNoun}`, { defaultValue: t("reader.askAbout.selection") })}
            </button>
            {props.renderLatex && selected && (
              <button
                type="button"
                className={styles.chip}
                data-testid={M.viewLatexChip}
                onClick={() => {
                  setLatexBlock(selected);
                  setLatexTab("blocks");
                  setPreviewMode("latex");
                  setPreview(true);
                }}
              >
                {t("reader.viewLatex")}
              </button>
            )}
            <button type="button" className={styles.chipClear} aria-label={t("reader.clearSelection")} data-testid={M.askChipClear} onClick={() => setSelected(null)}>
              <IconX size={14} />
            </button>
          </div>
        )}
        {refCard && !(review && changes.length > 0) ? (
          <section className={styles.refCard} data-testid={M.refCard} data-kind={refCard.kind} aria-label={t("reader.ref.label")}>
            <div className={styles.refHead}>
              <span className={styles.refKind}>
                {refCard.kind === "citation"
                  ? t("reader.ref.citation", { keys: refCard.keys.map((k) => `[${k}]`).join(" ") })
                  : refCard.kind === "footnote"
                    ? refCard.number ? t("reader.ref.footnoteN", { n: refCard.number }) : t("reader.ref.footnote")
                    : t("reader.ref.crossRef")}
              </span>
              <button type="button" className={styles.iconBtn} aria-label={t("reader.close")} onClick={() => setRefCard(null)}>
                <IconX size={18} />
              </button>
            </div>
            <div className={styles.refText}>
              {refCard.kind === "citation"
                ? refCard.entries.map((en) => <p key={en.key}>{en.text ?? t("reader.ref.noEntry", { key: en.key })}</p>)
                : refCard.kind === "footnote"
                  ? <p>{refCard.text || t("reader.ref.emptyNote")}</p>
                  : <p>{refCard.title}</p>}
            </div>
            <div className={styles.pair}>
              {(() => {
                const goId =
                  refCard.kind === "reference" ? refCard.targetId : refCard.kind === "citation" ? (blocks.find((b) => b.type === "bibliography")?.id ?? null) : null;
                const goLabel = refCard.kind === "reference" ? t("reader.ref.goTo", { what: refCard.title.split(" · ")[0] }) : t("reader.ref.goToReferences");
                return goId ? (
                  <button
                    type="button"
                    className={styles.secondary}
                    data-testid={M.refGoto}
                    onClick={() => {
                      setRefCard(null);
                      props.onScrollToBlock(goId);
                    }}
                  >
                    {goLabel}
                  </button>
                ) : (
                  <span />
                );
              })()}
              <button
                type="button"
                className={styles.secondary}
                data-testid={M.refCopy}
                onClick={() => {
                  const text =
                    refCard.kind === "citation" ? refCard.entries.map((en) => en.text ?? en.key).join("\n") : refCard.kind === "footnote" ? refCard.text : refCard.title;
                  void navigator.clipboard?.writeText(text).catch(() => {});
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1600);
                }}
              >
                {t("reader.copy")}
              </button>
            </div>
          </section>
        ) : review && changes.length > 0 ? (
          <section className={styles.changesCard} data-testid={M.changesCard} aria-label={t("reader.changes.label")}>
            <div className={styles.changesHead}>
              <IconSparkles size={16} />
              {/* The headline opens the current change: the only way to a removed block, which has no place in the page. */}
              <button type="button" className={styles.headlineBtn} data-testid={M.changesShow} onClick={() => current && setDiffFor(current.blockId)}>
                {changesHeadline(changes)}
              </button>
              <span className={styles.flex} />
              <button type="button" className={styles.stepBtn} aria-label={t("reader.changes.previous")} data-testid={M.changesPrev} disabled={step <= 0} onClick={() => goToChange(step - 1)}>
                <IconChevronLeft size={18} />
              </button>
              <span className={styles.stepCount}>
                {Math.min(step, changes.length - 1) + 1} / {changes.length}
              </span>
              <button type="button" className={styles.stepBtn} aria-label={t("reader.changes.next")} data-testid={M.changesNext} disabled={step >= changes.length - 1} onClick={() => goToChange(step + 1)}>
                <IconChevronRight size={18} />
              </button>
            </div>
            {review.detail.summary && <p className={styles.changesSummary}>{shown(review.detail.summary)}</p>}
            <div className={styles.pair}>
              <button type="button" className={styles.secondary} data-testid={M.changesUndoAll} disabled={busy || !review.detail.undoVersionId} onClick={() => void undoAll()}>
                {t("reader.changes.undoAll")}
              </button>
              <button type="button" className={styles.primary} data-testid={M.changesKeep} disabled={busy} onClick={keep}>
                {t("reader.changes.keep")}
              </button>
            </div>
          </section>
        ) : activeIssue ? (
          <section className={styles.issueCard} data-testid={M.issueCard} data-severity={activeIssue.severity} aria-label={t("reader.issue.label")}>
            <div className={styles.changesHead}>
              {activeIssue.severity === "error" ? <IconAlertCircle size={16} className={styles.errorIcon} /> : <IconAlertTriangle size={16} className={styles.warnIcon} />}
              <span className={styles.issueTitle}>
                {t("reader.issue.count", { n: (issueAt ?? 0) + 1, total: issues.length })}
              </span>
              <span className={styles.flex} />
              <button type="button" className={styles.stepBtn} aria-label={t("reader.issue.previous")} data-testid={M.issuePrev} disabled={(issueAt ?? 0) <= 0} onClick={() => goToIssue((issueAt ?? 0) - 1)}>
                <IconChevronLeft size={18} />
              </button>
              <button type="button" className={styles.stepBtn} aria-label={t("reader.issue.next")} data-testid={M.issueNext} disabled={(issueAt ?? 0) >= issues.length - 1} onClick={() => goToIssue((issueAt ?? 0) + 1)}>
                <IconChevronRight size={18} />
              </button>
              <button type="button" className={styles.stepBtn} aria-label={t("reader.close")} data-testid={M.issueClose} onClick={() => setIssueAt(null)}>
                <IconX size={18} />
              </button>
            </div>
            <p className={styles.issueMessage}>{activeIssue.message}</p>
            {!activeIssue.blockId && <p className={styles.muted}>{t("reader.issue.wholeDocNote")}</p>}
            <button type="button" className={styles.primary} data-testid={M.issueAsk} onClick={askAboutIssue}>
              {viewOnly ? t("reader.issue.askWhy") : t("reader.issue.askFix")}
            </button>
          </section>
        ) : (
          /* The Ask bar is a button, not an input: the keyboard opens in the sheet only. */
          <button
            type="button"
            className={styles.askBar}
            data-testid={M.askBar}
            onClick={() => {
              setAskPrefill(undefined);
              setSheet("ask");
            }}
          >
            <IconSparkles size={17} />
            <span>{viewOnly ? t("reader.askBar.reader") : t("reader.askBar.editor")}</span>
          </button>
        )}
      </div>

      {(sheet === "outline" || sheet === "ask") && (
        <div className={styles.backdrop} onClick={() => setSheet(null)} aria-hidden="true" />
      )}
      {sheet === "look" && (
        <div
          className={styles.backdropClear}
          onClick={() => {
            props.onLookDismiss?.();
            setSheet(null);
          }}
          aria-hidden="true"
        />
      )}
      {(sheet === "more" || sheet === "share" || sheet === "details" || sheet === "check" || sheet === "settings") && (
        <div className={styles.backdrop} onClick={() => setSheet(null)} aria-hidden="true" />
      )}
      {sheet === "more" && (
        <section className={styles.sheet} data-height="half" data-testid={M.moreSheet} aria-label={t("reader.more.label")}>
          <div className={styles.grip} aria-hidden="true" />
          <div className={styles.menuList} role="menu" data-testid={M.moreMenu}>
            {props.share && (
              <button type="button" role="menuitem" data-testid={M.moreShare} onClick={() => setSheet("share")}>
                {t("reader.more.share")}
              </button>
            )}
            {props.check && (
              <button type="button" role="menuitem" data-testid={M.moreCheck} onClick={() => setSheet("check")}>
                {t("reader.more.check")}
                <span className={styles.menuMeta} data-severity={errorCount ? "error" : issues.length ? "warning" : "ok"}>
                  {props.check.issues === null ? (props.check.running ? t("reader.more.checking") : "") : issueSummary(issues)}
                </span>
              </button>
            )}
            {props.renderLook && (
              <button type="button" role="menuitem" data-testid={M.moreLook} onClick={() => setSheet("look")}>
                {t("reader.more.look")}
              </button>
            )}
            {props.renderSettings && (
              <button type="button" role="menuitem" data-testid={M.moreSettings} onClick={() => setSheet("settings")}>
                {t("reader.more.settings")}
              </button>
            )}
            <AppearanceChoice />
            <button type="button" role="menuitem" data-testid={M.moreDetails} onClick={() => setSheet("details")}>
              {t("reader.more.details")}
            </button>
            <button
              type="button"
              role="menuitem"
              data-testid={M.allDocs}
              onClick={() => {
                setSheet(null);
                props.onBack();
              }}
            >
              {t("reader.more.allDocuments")}
            </button>
          </div>
        </section>
      )}
      {sheet === "share" && props.share && (
        <section className={styles.sheet} data-height="full" aria-label={t("reader.share")}>
          <div className={styles.sheetHead}>
            <div className={styles.grip} aria-hidden="true" />
            <span className={styles.sheetTitle}>{t("reader.share")}</span>
            <button type="button" className={styles.iconBtn} aria-label={t("reader.close")} onClick={() => setSheet(null)}>
              <IconX size={20} />
            </button>
          </div>
          <div className={styles.sheetBody}>
            <MobileShareSheet
              documentId={props.documentId}
              canManage={props.share.canManage}
              ownerId={props.share.ownerId}
              ownerName={props.share.ownerName}
              initial={props.share.initial}
              onCopied={() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 1600);
              }}
            />
          </div>
        </section>
      )}
      {sheet === "details" && (
        <section className={styles.sheet} data-height="half" data-testid={M.detailsSheet} aria-label={t("reader.details.title")}>
          <div className={styles.sheetHead}>
            <div className={styles.grip} aria-hidden="true" />
            <span className={styles.sheetTitle}>{t("reader.details.title")}</span>
            <button type="button" className={styles.iconBtn} aria-label={t("reader.close")} onClick={() => setSheet(null)}>
              <IconX size={20} />
            </button>
          </div>
          <dl className={styles.details}>
            <dt>{t("reader.details.titleField")}</dt>
            <dd>{title || t("docs.untitled")}</dd>
            {props.details?.owner && (
              <>
                <dt>{t("reader.details.owner")}</dt>
                <dd>{props.details.owner}</dd>
              </>
            )}
            {props.details?.updatedAt && (
              <>
                <dt>{t("reader.details.lastEdited")}</dt>
                <dd>{new Date(props.details.updatedAt).toLocaleString()}</dd>
              </>
            )}
            {typeof props.details?.words === "number" && (
              <>
                <dt>{t("reader.details.words")}</dt>
                <dd>{props.details.words.toLocaleString()}</dd>
              </>
            )}
            <dt>{t("reader.details.access")}</dt>
            <dd>{viewOnly ? t("reader.details.canRead") : t("reader.details.canChange")}</dd>
          </dl>
        </section>
      )}
      {sheet === "look" && props.renderLook && (
        <section className={styles.sheet} data-height="half" aria-label={t("reader.look")}>
          <div className={styles.sheetHead}>
            <div className={styles.grip} aria-hidden="true" />
            <span className={styles.sheetTitle}>{t("reader.look")}</span>
            <span className={styles.flex} />
            {/* ✕ as on the other sheets (Olivia, 10 Oct): like Cancel, the saved look comes back. */}
            <button
              type="button"
              className={styles.iconBtn}
              aria-label={t("reader.close")}
              data-testid={M.lookClose}
              onClick={() => {
                props.onLookDismiss?.();
                setSheet(null);
              }}
            >
              <IconX size={20} />
            </button>
          </div>
          <div className={styles.sheetBody}>{props.renderLook({ close: () => setSheet(null) })}</div>
        </section>
      )}
      {sheet === "settings" && props.renderSettings && (
        <section className={styles.sheet} data-height="full" aria-label={t("reader.settingsLabel")}>
          <div className={styles.sheetHead}>
            <div className={styles.grip} aria-hidden="true" />
            <span className={styles.sheetTitle}>{t("reader.settingsTitle")}</span>
            <button type="button" className={styles.iconBtn} aria-label={t("reader.close")} onClick={() => setSheet(null)}>
              <IconX size={20} />
            </button>
          </div>
          <div className={styles.sheetBody}>{props.renderSettings()}</div>
        </section>
      )}
      {sheet === "check" && props.check && (
        <section className={styles.sheet} data-height="full" data-testid={M.checkSheet} aria-label={t("reader.check.title")}>
          <div className={styles.sheetHead}>
            <div className={styles.grip} aria-hidden="true" />
            <span className={styles.sheetTitle}>{t("reader.check.title")}</span>
            <button type="button" className={styles.iconBtn} aria-label={t("reader.close")} onClick={() => setSheet(null)}>
              <IconX size={20} />
            </button>
          </div>
          <div className={styles.sheetBody}>
            <p className={styles.checkSummary} data-testid={M.checkSummary}>
              {props.check.running
                ? t("reader.check.checking")
                : props.check.failed
                  ? t("reader.check.failed")
                  : props.check.issues === null
                    ? t("reader.check.notYet")
                    : issues.length === 0
                      ? t("reader.check.clean")
                      : issueSummary(issues)}
            </p>
            {(["error", "warning"] as const).map((sev) => {
              const group = issues.map((iss, i) => ({ iss, i })).filter(({ iss }) => iss.severity === sev);
              if (!group.length) return null;
              return (
                <section key={sev} className={styles.issueGroup}>
                  <h4>{sev === "error" ? t("reader.check.errors") : t("reader.check.warnings")}</h4>
                  <ul>
                    {group.map(({ iss, i }) => (
                      <li key={i}>
                        <button
                          type="button"
                          className={styles.issueRow}
                          data-testid={M.checkIssue}
                          data-severity={iss.severity}
                          data-block-id={iss.blockId ?? undefined}
                          onClick={() => goToIssue(i)}
                        >
                          {sev === "error" ? <IconAlertCircle size={17} className={styles.errorIcon} /> : <IconAlertTriangle size={17} className={styles.warnIcon} />}
                          <span className={styles.issueText}>
                            <span>{iss.message}</span>
                            <span className={styles.muted}>{(iss.blockId && blockLocation(blocks, iss.blockId)) || t("reader.check.wholeDocument")}</span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
            {props.publisherCheck && (
              <section className={styles.issueGroup} data-testid={M.checkPublisher} data-status={props.publisherCheck.status}>
                <h4>{t("reader.check.publisher", { publisher: props.publisherCheck.publisher })}</h4>
                {props.publisherCheck.findings.length === 0 ? (
                  <p className={styles.muted}>{t("reader.check.publisherReady", { publisher: props.publisherCheck.publisher })}</p>
                ) : (
                  <ul>
                    {props.publisherCheck.findings.map((f, i) => (
                      <li key={`${f.code}-${i}`} className={styles.issueRow} data-testid={M.checkPublisherFinding} data-severity={f.severity} data-code={f.code}>
                        {f.severity === "error" ? <IconAlertCircle size={17} className={styles.errorIcon} /> : <IconAlertTriangle size={17} className={styles.warnIcon} />}
                        <span className={styles.issueText}><span>{f.message}</span></span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}
            <button type="button" className={`${styles.secondary} ${styles.checkAgain}`} data-testid={M.checkRerun} disabled={props.check.running} onClick={props.check.run}>
              {t("reader.check.again")}
            </button>
          </div>
        </section>
      )}
      {sheet === "outline" && (
        <section className={styles.sheet} data-height="half" data-testid={M.outlineSheet} aria-label={t("reader.top.outline")}>
          <div className={styles.grip} aria-hidden="true" />
          <div className={styles.sheetBody}>{props.renderOutline(go)}</div>
        </section>
      )}
      {sheet === "ask" && (
        <section className={styles.sheet} data-height={arrivalHalf ? "half" : "full"} data-testid={M.askSheet} aria-label={t("reader.ask.label")}>
          <div className={styles.sheetHead}>
            <div className={styles.grip} aria-hidden="true" />
            {viewOnly && <span className={styles.sheetNote}>{t("reader.ask.answersOnly")}</span>}
            <button type="button" className={styles.newChat} data-testid={M.askNewChat} onClick={() => window.dispatchEvent(new CustomEvent(ASK_NEW_CHAT_EVENT))}>
              {t("reader.ask.newChat")}
            </button>
            <button type="button" className={styles.iconBtn} aria-label={t("reader.ask.close")} data-testid={M.askSheetClose} onClick={() => setSheet(null)}>
              <IconX size={20} />
            </button>
          </div>
          <div className={styles.sheetBody} key={askPrefill ?? ""}>
            {props.renderAsk({ context, clearContext: () => setSelected(null), close: () => setSheet(null), prefill: askPrefill, autoFocus: !arrivalHalf })}
          </div>
        </section>
      )}

      {diff && (
        <>
          <div className={styles.backdrop} onClick={() => setDiffFor(null)} aria-hidden="true" />
          <section className={styles.sheet} data-height="half" data-testid={M.changeDiff} aria-label={t("reader.changes.beforeAfter")}>
            <div className={styles.sheetHead}>
              <div className={styles.grip} aria-hidden="true" />
              <span className={styles.sheetNote}>{blockLocation(blocks, diff.blockId) ?? nounLabel(diff.type)}</span>
              <button type="button" className={styles.iconBtn} aria-label={t("reader.close")} onClick={() => setDiffFor(null)}>
                <IconX size={20} />
              </button>
            </div>
            <div className={styles.diffBody}>
              {diff.kind !== "added" && (
                <div className={styles.diffPart}>
                  <span className={styles.diffLabel}>{t("reader.changes.before")}</span>
                  <del className={styles.diffBefore}>{shown(blockText(diff.before)) || `(${nounLabel(diff.type)})`}</del>
                </div>
              )}
              {diff.kind !== "removed" && (
                <div className={styles.diffPart}>
                  <span className={styles.diffLabel}>{diff.kind === "added" ? t("reader.changes.addedLabel") : t("reader.changes.after")}</span>
                  <p className={styles.diffAfter}>{liveText(diff.blockId, blockText(diff.after)) || `(${nounLabel(diff.type)})`}</p>
                </div>
              )}
              {diff.kind === "removed" && <p className={styles.diffNote}>{t("reader.changes.removedNote")}</p>}
              <div className={styles.pair}>
                {diff.kind === "removed" ? (
                  <button
                    type="button"
                    className={styles.secondary}
                    data-testid={M.changeCopy}
                    onClick={() => {
                      void navigator.clipboard?.writeText(shown(blockText(diff.before))).catch(() => {});
                      setCopied(true);
                      setTimeout(() => setCopied(false), 1600);
                    }}
                  >
                    {t("reader.changes.copyText")}
                  </button>
                ) : (
                  <button type="button" className={styles.secondary} data-testid={M.changeUndo} disabled={busy} onClick={() => void undoOne(diff)}>
                    {t("reader.changes.undoThis")}
                  </button>
                )}
                <button
                  type="button"
                  className={styles.primary}
                  data-testid={M.changeNext}
                  disabled={changes.length < 2}
                  onClick={() => {
                    const i = changes.findIndex((c) => c.blockId === diff.blockId);
                    const next = changes[(i + 1) % changes.length];
                    goToChange((i + 1) % changes.length);
                    setDiffFor(next.blockId);
                  }}
                >
                  {t("reader.changes.next")}
                </button>
              </div>
            </div>
          </section>
        </>
      )}

      {viewer && (
        <section className={styles.viewer} data-testid={M.viewer} data-type={viewer.type} aria-label={t("reader.viewer.label")}>
          <header className={styles.topbar}>
            <button type="button" className={styles.iconBtn} aria-label={t("reader.close")} data-testid={M.viewerClose} onClick={() => setViewer(null)}>
              <IconX size={22} />
            </button>
            <div className={styles.titleBlock}>
              <span className={styles.title}>{(blockLocation(blocks, viewer.id) ?? nounLabel(viewer.type)).replace(/^./, (c) => c.toUpperCase())}</span>
              <span className={styles.titleSub}>{zoomed ? t("reader.viewer.hintFit") : t("reader.viewer.hintEnlarge")}</span>
            </div>
          </header>
          <div
            className={styles.viewerBody}
            onDoubleClick={() => setZoomed((z) => !z)}
            // Swiping down from the top closes it, so a reader zoomed past the ✕ is never stuck.
            // Touch events, not pointer events: a vertical swipe is a pan the browser takes
            // over (pointercancel, no pointerup), but touchend still arrives.
            onTouchStart={(e) => {
              const t = e.touches[0];
              swipeStart.current = e.touches.length === 1 && t ? { y: t.clientY, x: t.clientX, top: e.currentTarget.scrollTop } : null;
            }}
            onTouchEnd={(e) => {
              const s0 = swipeStart.current;
              const t = e.changedTouches[0];
              swipeStart.current = null;
              if (s0 && t && s0.top <= 0 && t.clientY - s0.y > 90 && Math.abs(t.clientX - s0.x) < 60) setViewer(null);
            }}
          >
            {/* The page's own rendering of this block (our DOM), so it looks exactly as it reads. */}
            <div className={styles.viewerContent} data-zoomed={zoomed || undefined} dangerouslySetInnerHTML={{ __html: viewer.html }} />
          </div>
          <div className={styles.viewerFoot}>
            <button
              type="button"
              className={styles.askBar}
              data-testid={M.viewerAsk}
              onClick={() => {
                setSelected(viewer.id);
                setViewer(null);
                setSheet("ask");
              }}
            >
              <IconSparkles size={17} />
              <span>{t(`reader.askAbout.${blockNoun(viewer.type)}`)}</span>
            </button>
          </div>
        </section>
      )}

      {preview && (
        <section className={styles.preview} data-testid={M.preview} aria-label={t("reader.preview.label")}>
          <header className={styles.topbar}>
            <button type="button" className={styles.iconBtn} aria-label={t("reader.preview.close")} data-testid={M.previewClose} onClick={() => setPreview(false)}>
              <IconChevronLeft size={22} />
            </button>
            <div className={styles.titleBlock}>
              <span className={styles.title}>{t("reader.preview.label")}</span>
              <span className={styles.titleSub}>{title}</span>
            </div>
          </header>
          {props.renderLatex && (
            // One row: PDF | Preamble · Code · Blocks (Ḥassān, 18 Oct): no PDF/LaTeX switch, no second row.
            <div className={styles.previewTabs} role="tablist" aria-label={t("reader.preview.as")} data-testid={testIds.latexView.tabs}>
              <button type="button" role="tab" aria-selected={previewMode === "pdf"} data-testid={M.previewMode} data-mode="pdf" onClick={() => setPreviewMode("pdf")}>
                {t("reader.preview.pdf")}
              </button>
              <span className={styles.previewTabsRule} aria-hidden="true" />
              {LATEX_TABS.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  role="tab"
                  aria-selected={previewMode === "latex" && latexTab === tab}
                  data-testid={testIds.latexView.tab}
                  data-tab={tab}
                  onClick={() => { setLatexTab(tab); setPreviewMode("latex"); }}
                >
                  {i18n.t(`editor:latexView.tabs.${tab}`)}
                </button>
              ))}
            </div>
          )}
          <div className={styles.previewBody}>
            {previewMode === "latex" && props.renderLatex
              ? props.renderLatex({ highlightBlockId: latexBlock, tab: latexTab, onTab: setLatexTab })
              : props.renderPreview({
                  seeIssues: () => {
                    setPreview(false);
                    setSheet("check");
                  },
                })}
          </div>
        </section>
      )}
    </div>
  );
}
